<i18n src="./resources/locales.json" />

<template>
  <v-dialog v-model="dialog" persistent max-width="460" content-class="contact-selection-dialog">
    <v-card color="surface lighten-1">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <v-card-title class="header" v-html="title"></v-card-title>

      <div class="search-bar">
        <v-text-field
          ref="search"
          v-model="searchString"
          prepend-inner-icon="mdi-magnify"
          single-line
          hide-details
          solo
          flat
          clearable
          :placeholder="$t('search.placeholder')"
          height="40"
          class="search-contacts"
          @keypress.enter="search"
        />
      </div>
      <v-progress-linear v-show="isLoading" height="3" indeterminate class="loading" />

      <v-list color="var(--v-surface-base)" class="dialog-content pa-0">
        <v-fade-transition hide-on-leave>
          <EcsEmptySearchMessage
            v-if="!isLoading && contacts.length === 0 && lastSearchString === searchString"
            :message="$t('search.empty', { searchString })"
          />
        </v-fade-transition>

        <v-virtual-scroll v-show="contacts.length" :items="contacts" :item-height="56" :bench="1" :height="460">
          <template v-slot="{ item }">
            <v-list-item @click="toggleSelectedItem(item)">
              <v-list-item-avatar>
                <EcsAvatar :contact-id="item.id" :alt="item.displayName" :size="40" />
              </v-list-item-avatar>
              <v-list-item-title>{{ item.displayName }}</v-list-item-title>
              <v-list-item-action>
                <v-checkbox
                  :value="item.id"
                  v-model="selected"
                  @click.stop="() => {}"
                  v-if="!disabledContacts.includes(item.id)"
                />
                <v-checkbox v-else v-model="checked" disabled />
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-virtual-scroll>
      </v-list>

      <v-card-actions>
        <v-btn v-show="selected.length" text @click="clearSelection">
          {{ $t('buttons.clear') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">
          {{ $t('buttons.cancel') }}
        </v-btn>
        <v-btn class="primary" @click="() => (selected.length ? select() : cancel())" :loading="isLoadingAction">
          {{ selected.length ? $t('buttons.addN', { n: selected.length }) : $t('buttons.add') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsAvatar from '@/components/ecs-avatar/EcsAvatar';
import EcsEmptySearchMessage from '@/components/ecs-empty-search-message/EcsEmptySearchMessage';

export default {
  name: 'EcsContactSelectionDialog',

  components: { EcsAvatar, EcsEmptySearchMessage },

  data() {
    return {
      title: '',
      searchString: '',
      lastSearchString: '',
      selected: [],
      dialog: false,
      isLoading: false,
      isLoadingAction: false,
      disabledContacts: [],
      checked: true,
      itemsPerPage: 50,
      pageNumber: 1
    };
  },

  computed: {
    ...mapGetters(['contacts'])
  },

  mounted() {
    this.$root.$on('open-contact-selection-dialog', this.open);
  },

  beforeDestroy() {
    this.$root.$off('open-contact-selection-dialog');
  },

  methods: {
    ...mapActions(['searchContacts', 'clearContacts']),

    search() {
      if (this.lastSearchString !== this.searchString) {
        this.clearContacts();
      }

      this.isLoading = true;
      this.lastSearchString = this.searchString;
      this.searchContacts({
        query: {
          textToSearch: this.searchString,
          maxNumberOfResults: this.itemsPerPage,
          pageNumber: this.pageNumber
        }
      }).finally(() => {
        this.isLoading = false;
      });
    },

    async open({ title, action, disabledContacts }) {
      this.dialog = true;
      this.title = title || this.$t('header.title');
      this.disabledContacts = disabledContacts;
      this.clearContacts();
      await this.$nextTick();
      this.$refs.search.focus();
      this.search();
      this.$once('selected', selection => {
        this.isLoadingAction = true;
        action(selection)
          .then(this.close)
          .finally(() => (this.isLoadingAction = false));
      });
    },

    toggleSelectedItem({ id }) {
      if (this.disabledContacts.includes(id)) {
        return;
      }

      const isSelected = this.selected.some(item => item === id);
      if (isSelected) {
        this.selected = this.selected.filter(selectedId => selectedId !== id);
      } else {
        this.selected.push(id);
      }
    },

    select() {
      this.$emit('selected', [...this.selected]);
    },

    clearSelection() {
      this.selected = [];
    },

    close() {
      this.dialog = false;
      this.selected = [];
      this.disabledContacts = [];
      this.searchString = '';
      this.lastSearchString = '';
      this.$off('selected');
    },

    cancel() {
      this.close();
    }
  }
};
</script>

<style scoped lang="scss">
.search-bar {
  background-color: var(--v-surface-lighten1);
  padding: 16px;
}

.loading {
  margin-top: -3px;
}

.dialog-content {
  height: 460px;
}
</style>

<style lang="scss">
.contact-selection-dialog {
  .header b {
    color: var(--v-primary-base);
    padding: 0 8px;
  }
}

.search-contacts .v-input__control {
  min-height: 40px !important;
}

.search-contacts .v-input__slot {
  background-color: var(--v-surface-base) !important;
  border-radius: 20px;
  min-height: 40px;
}
</style>
